import React from 'react';
import {
    A,
    Typography,
} from '@saddlebackchurch/react-cm-ui';
import makeStyles from '@saddlebackchurch/react-cm-ui/core/styles/makeStyles';
import ClassNames from 'classnames';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
import DonutChart from '../../../global/donutChart.jsx';
import { INTL_CAMPUS_IDS } from '../shared/js/constants';
import { formatNumberAsCurrency } from '../shared/js/utils';
import { CampaignCommitment } from '../shared/models';
import CommitmentTitle from './commitmentTitle';
import { BEM_BLOCK_NAME } from './constants';

type PropTypes = {
    campusId?: number;
    commitment: CampaignCommitment;
    locale: string;
    onEditGoalClicked: () => void;
};

const useStyles = makeStyles(({
    breakpoints,
    palette,
    spacing,
    typography,
}) => ({
    root: {},
    amountsContainer: {
        flex: '1',
    },
    amountsContainerContent: {
        display: 'flex',
        flexFlow: 'row wrap',
    },
    amountsDisclaimer: {
        marginTop: spacing(2),
    },
    amountLabel: {
        lineHeight: 1,
        color: palette.text.secondary,
        marginBottom: spacing(0.5),
    },
    amountValue: {
        fontWeight: typography.fontWeightBold,
        lineHeight: 1,
    },
    dataContainer: {
        display: 'flex',
        flexFlow: 'row wrap',
        gap: spacing(3),
        marginTop: spacing(2),
    },
    donutChart: {
        paddingLeft: spacing(1),
        paddingTop: spacing(0.5),
    },
    donutChartContainer: {
        flex: '0 0 118px',
    },
    editLinkButton: {
        display: 'block',
        fontSize: typography.pxToRem(14),
    },
    fundedContainer: {
        flex: '1 1 50%',
        [breakpoints.between(800, 1199)]: {
            flex: '1 1 25%',
        },
    },
    goalContainer: {
        flex: '1 1 50%',
        marginBottom: spacing(3),
        [breakpoints.between(800, 1199)]: {
            flex: '1 1 25%',
            marginBottom: 0,
        },
    },
    remainingContainer: {
        flex: '1 1 50%',
        [breakpoints.between(800, 1199)]: {
            flex: '1 1 25%',
        },
    },
    scheduledContainer: {
        flex: '1 1 50%',
        [breakpoints.between(800, 1199)]: {
            flex: '1 1 25%',
        },
    },
    titleContainer: {
        display: 'flex',
    },
    titleContainerLeftColumn: {
        flex: '1 1 50%',
    },
    titleContainerRightColumn: {
        flex: '1 1 50%',
        textAlign: 'right',
    },
}));

const CLASS_NAME_ROOT: string = `${BEM_BLOCK_NAME}--existing_commitment_container`;
const CLASS_NAME_AMOUNTS_CONTAINER: string = `${BEM_BLOCK_NAME}--existing_commitment_amounts_container`;
const CLASS_NAME_AMOUNTS_CONTAINER_CONTENT: string = `${BEM_BLOCK_NAME}--existing_commitment_amounts_container_content`;
const CLASS_NAME_AMOUNTS_DISCLAIMER: string = `${BEM_BLOCK_NAME}--existing_commitment_amounts_disclaimer`;
const CLASS_NAME_AMOUNT_LABEL: string = `${BEM_BLOCK_NAME}--existing_commitment_amount_label`;
const CLASS_NAME_AMOUNT_VALUE: string = `${BEM_BLOCK_NAME}--existing_commitment_amount_value`;
const CLASS_NAME_DATA_CONTAINER: string = `${BEM_BLOCK_NAME}--existing_commitment_data_container`;
const CLASS_NAME_DONUT_CHART: string = `${BEM_BLOCK_NAME}--existing_commitment_donut_chart`;
const CLASS_NAME_DONUT_CHART_CONTAINER: string = `${BEM_BLOCK_NAME}--existing_commitment_donut_chart_container`;
const CLASS_NAME_EDIT_LINK_BUTTON: string = `${BEM_BLOCK_NAME}--existing_commitment_edit_link_button`;
const CLASS_NAME_FUNDED_CONTAINER: string = `${BEM_BLOCK_NAME}--existing_commitment_funded_container`;
const CLASS_NAME_GOAL_CONTAINER: string = `${BEM_BLOCK_NAME}--existing_commitment_goal_container`;
const CLASS_NAME_REMAINING_CONTAINER: string = `${BEM_BLOCK_NAME}--existing_commitment_remaining_container`;
const CLASS_NAME_SCHEDULED_CONTAINER: string = `${BEM_BLOCK_NAME}--existing_commitment_scheduled_container`;
const CLASS_NAME_TITLE_CONTAINER: string = `${BEM_BLOCK_NAME}--existing_commitment_title_container`;
const CLASS_NAME_TITLE_CONTAINER_LEFT_COLUMN: string = `${BEM_BLOCK_NAME}--existing_commitment_title_container_left_col`;
const CLASS_NAME_TITLE_CONTAINER_RIGHT_COLUMN: string = `${BEM_BLOCK_NAME}--existing_commitment_title_container_tight_col`;

function ViewCommitment({
    campusId,
    commitment,
    locale,
    onEditGoalClicked,
}: PropTypes) {
    const classes = useStyles();

    const isInternationalCampus = !isNil(campusId) && INTL_CAMPUS_IDS.includes(campusId);

    const goalAmount = commitment.goal.amount;
    const amountFunded = commitment.funded?.amount ?? 0;
    const amountScheduled = commitment.scheduled?.amount ?? 0;
    const amountRemaining = commitment.remaining?.amount ?? goalAmount - amountFunded;

    const rootClassNames = ClassNames(classes.root, CLASS_NAME_ROOT);
    const amountLabelClassNames = ClassNames(classes.amountLabel, CLASS_NAME_AMOUNT_LABEL);
    const amountValueClassNames = ClassNames(classes.amountValue, CLASS_NAME_AMOUNT_VALUE);
    const dataContainerClassNames = ClassNames(classes.dataContainer, CLASS_NAME_DATA_CONTAINER);
    const donutChartClassNames = ClassNames(classes.donutChart, CLASS_NAME_DONUT_CHART);

    const { t } = useTranslation();

    const donutChartContainerClassNames = ClassNames(
        classes.donutChartContainer,
        CLASS_NAME_DONUT_CHART_CONTAINER,
    );

    const amountsContainerClassNames = ClassNames(
        classes.amountsContainer,
        CLASS_NAME_AMOUNTS_CONTAINER,
    );

    const amountsContainerContentClassNames = ClassNames(
        classes.amountsContainerContent,
        CLASS_NAME_AMOUNTS_CONTAINER_CONTENT,
    );

    const fundedContainerClassNames = ClassNames(
        classes.fundedContainer,
        CLASS_NAME_FUNDED_CONTAINER,
    );

    const editLinkButtonClassNames = ClassNames(
        classes.editLinkButton,
        CLASS_NAME_EDIT_LINK_BUTTON,
    );

    const goalContainerClassNames = ClassNames(classes.goalContainer, CLASS_NAME_GOAL_CONTAINER);

    const remainingContainerClassNames = ClassNames(
        classes.remainingContainer,
        CLASS_NAME_REMAINING_CONTAINER,
    );

    const scheduledContainerClassNames = ClassNames(
        classes.scheduledContainer,
        CLASS_NAME_SCHEDULED_CONTAINER,
    );

    return (
        <div
            className={rootClassNames}
            data-testid={`${BEM_BLOCK_NAME}--view_container`}
        >
            <div className={ClassNames(classes.titleContainer, CLASS_NAME_TITLE_CONTAINER)}>
                <div
                    className={ClassNames(
                        classes.titleContainerLeftColumn,
                        CLASS_NAME_TITLE_CONTAINER_LEFT_COLUMN,
                    )}
                >
                    <CommitmentTitle />
                </div>
                <div
                    className={ClassNames(
                        classes.titleContainerRightColumn,
                        CLASS_NAME_TITLE_CONTAINER_RIGHT_COLUMN,
                    )}
                >
                    <A
                        className={editLinkButtonClassNames}
                        onClick={onEditGoalClicked}
                    >
                        {t('viewCommitment.interactiveLinks.edit')}
                    </A>
                </div>
            </div>

            <div
                className={dataContainerClassNames}
                data-testid={`${BEM_BLOCK_NAME}--goal_container`}
            >
                {/* Donut Chart (Funded vs. Remaining) */}
                <div
                    className={donutChartContainerClassNames}
                    data-testid={`${BEM_BLOCK_NAME}--donut_container`}
                >
                    {/* TODO: Should we expose any props to allow this to be customized by a widget consumer? */}
                    <DonutChart
                        className={donutChartClassNames}
                        color1="#00aeef" // cyan[500]
                        color2="#edf1f5" // grey[200]
                        height={118}
                        innerSize={59}
                        options={{
                            plotOptions: {
                                pie: {
                                    slicedOffset: 0,
                                },
                            },
                        }}
                        value1={amountFunded}
                        value2={amountRemaining}
                        width={118}
                    />
                </div>

                <div className={amountsContainerClassNames}>
                    <div className={amountsContainerContentClassNames}>
                        {/* eslint-disable max-len */}
                        {/* Goal Amount */}
                        <div className={goalContainerClassNames}>
                            <Typography
                                className={amountLabelClassNames}
                                component="div"
                                variant="caption"
                            >
                                {t('viewCommitment.titles.commitment')}
                            </Typography>

                            <Typography
                                className={amountValueClassNames}
                                component="div"
                                variant="body1"
                            >
                                {formatNumberAsCurrency(commitment.goal.amount, commitment.goal.currency, locale)}
                            </Typography>
                        </div>

                        {/* Funded Amount */}
                        <div className={fundedContainerClassNames}>
                            <Typography
                                className={amountLabelClassNames}
                                component="div"
                                variant="caption"
                            >
                                {t('viewCommitment.titles.funded')}
                            </Typography>

                            <Typography
                                className={amountValueClassNames}
                                component="div"
                                variant="body1"
                            >
                                {formatNumberAsCurrency(amountFunded, commitment.goal.currency, locale)}
                            </Typography>
                        </div>

                        {/* Remaining Amount */}
                        <div className={remainingContainerClassNames}>
                            <Typography
                                className={amountLabelClassNames}
                                component="div"
                                variant="caption"
                            >
                                {t('viewCommitment.titles.remaining')}
                            </Typography>

                            <Typography
                                className={amountValueClassNames}
                                component="div"
                                variant="body1"
                            >
                                {formatNumberAsCurrency(amountRemaining, commitment.goal.currency, locale)}
                            </Typography>
                        </div>

                        {/* Scheduled Amount */}
                        {!isInternationalCampus && (
                            <div className={scheduledContainerClassNames}>
                                <Typography
                                    className={amountLabelClassNames}
                                    component="div"
                                    variant="caption"
                                >
                                    {t('viewCommitment.titles.scheduled')}
                                </Typography>

                                <Typography
                                    className={amountValueClassNames}
                                    component="div"
                                    variant="body1"
                                >
                                    {formatNumberAsCurrency(amountScheduled, commitment.goal.currency, locale)}
                                </Typography>
                            </div>
                        )}

                        {/* eslint-disable max-len */}
                    </div>

                    <Typography
                        className={ClassNames(classes.amountsDisclaimer, CLASS_NAME_AMOUNTS_DISCLAIMER)}
                        component="div"
                        variant="body2"
                    >
                        {isInternationalCampus ?
                            t('viewCommitment.titles.asteriskInfo_oneWeek') :
                            t('viewCommitment.titles.asteriskInfo')}
                    </Typography>
                </div>
            </div>
        </div>

    );
}

export default ViewCommitment;
